import { ApolloClient, useApolloClient } from "@apollo/client";
import { assertNever, getDataOrNull } from "@msys/common";
import { isUndefined, reverse } from "lodash-es";
import { getUrlSearchParamsByParamsName } from "../../commons/hooks/useStateWithUrlParams.js";
import { BreadcrumbItem } from "../../commons/layout/PageBreadcrumbs.js";
import {
  PimProductPropertyInput,
  Scalars,
} from "../../../clients/graphqlTypes.js";
import {
  productFiltersDefaultValue,
  ProductFiltersFormValues,
} from "./filters/ProductFilterFields.js";
import {
  ItemProductSearchFilterDefaultsFragment,
  ItemProductSearchFilterDefinitionsFragment,
} from "./productSearchFilters.generated.js";
import {
  GetProductFullDescription_ProductFragment,
  GetProductFullDescription_ProductSearchResultFragment,
  usePimAddSupplierProductViewedMutation,
  useProductBreadcrumbItems__ProductCategoryTreeQuery,
} from "./helper.generated.js";
import { PimProductPropertyFragment } from "./Product.generated.js";
import {
  usePimGetSupplierCatalogue__OrganisationWriteAccessQuery,
  usePimSearchSupplierCatalogues__OrganisationWriteAccessQuery,
} from "./SupplierCatalogueAccess.generated.js";
import React, { useCallback, useEffect } from "react";
import { WithProductInfoFragment } from "../doc-items/Fragments.generated.js";

export function pimProductPropertyToInput(
  property: PimProductPropertyFragment
): PimProductPropertyInput {
  switch (property.__typename) {
    case "PimProductPropertyBool": {
      return {
        bool: {
          key: property.prop2.key,
          label: property.prop2.label,
          clientVisibility: property.prop2.clientVisibility,
          valueBool: property.prop2.valueBool,
        },
      };
    }
    case "PimProductPropertyNumber": {
      return {
        number: {
          key: property.prop2.key,
          label: property.prop2.label,
          clientVisibility: property.prop2.clientVisibility,
          valueNumber: property.prop2.valueNumber,
          unit: property.prop2.unit,
        },
      };
    }
    case "PimProductPropertyText": {
      return {
        text: {
          key: property.prop2.key,
          label: property.prop2.label,
          allowedValuesText: property.prop2.allowedValuesText.map(
            t => t.allowedText
          ),
          clientVisibility: property.prop2.clientVisibility,
          valueText: property.prop2.valueText,
        },
      };
    }
    case "PimProductPropertyRange": {
      return {
        range: {
          key: property.prop2.key,
          label: property.prop2.label,
          valueRange: property.pimProperty.valueRange,
          clientVisibility: property.prop2.clientVisibility,
          unit: property.prop2.unit,
        },
      };
    }
    case "PimProductPropertyBoolTyped": {
      return {
        boolTyped: {
          productTypeKey: property.pimProperty.productTypeKey,
          propertyTypeKey: property.pimProperty.propertyTypeKey,
          productTypeSystemKey: property.pimProperty.productTypeSystemKey,
          clientVisibility: property.prop2.clientVisibility,
          valueBool: property.prop2.valueBool,
        },
      };
    }
    case "PimProductPropertyNumberTyped": {
      return {
        numberTyped: {
          productTypeKey: property.pimProperty.productTypeKey,
          propertyTypeKey: property.pimProperty.propertyTypeKey,
          productTypeSystemKey: property.pimProperty.productTypeSystemKey,
          clientVisibility: property.prop2.clientVisibility,
          valueNumber: property.prop2.valueNumber,
          unit: property.pimProperty.unit,
        },
      };
    }
    case "PimProductPropertyTextTyped": {
      return {
        textTyped: {
          productTypeKey: property.pimProperty.productTypeKey,
          propertyTypeKey: property.pimProperty.propertyTypeKey,
          propertyTypeValueKey: property.prop2.valueText
            ? (property.pimProperty.allowedPropertyTypeValues.find(
                v => v.label === property.prop2.valueText
              )?.key ?? null)
            : null,
          productTypeSystemKey: property.pimProperty.productTypeSystemKey,
          clientVisibility: property.prop2.clientVisibility,
        },
      };
    }
    case "PimProductPropertyRangeTyped": {
      return {
        rangeTyped: {
          productTypeKey: property.pimProperty.productTypeKey,
          propertyTypeKey: property.pimProperty.propertyTypeKey,
          productTypeSystemKey: property.pimProperty.productTypeSystemKey,
          valueRange: property.pimProperty.valueRange,
          clientVisibility: property.prop2.clientVisibility,
          unit: property.pimProperty.unit,
        },
      };
    }
    default:
      throw assertNever(property);
  }
}

export function getProductFullDescription(
  product:
    | GetProductFullDescription_ProductFragment
    | GetProductFullDescription_ProductSearchResultFragment
) {
  const description = product.texts?.description?.trim();
  const extendedDescription = product.texts?.extendedDescription?.trim();
  return [
    description && !extendedDescription?.startsWith(description)
      ? description
      : null,
    extendedDescription,
  ]
    .filter(value => value)
    .join("\n");
}

/**
 * Reads a product's brand label across the two product shapes this app renders:
 * PIM *search results* expose a scalar `brand`, while resolved PIM *products*
 * (`Product`) expose it as `branding.brand.title`.
 */
export function getProductBrand(
  product:
    | { brand?: string | null }
    | { branding?: { brand?: { title?: string | null } | null } | null }
): string | null {
  if ("brand" in product) return product.brand ?? null;
  if ("branding" in product) return product.branding?.brand?.title ?? null;
  return null;
}

export function definitionsToFormValues(
  definitions:
    | ItemProductSearchFilterDefaultsFragment
    | ItemProductSearchFilterDefinitionsFragment,
  item?: WithProductInfoFragment & {
    pendingChangeAttributes: Scalars["JSON"]["output"];
  }
): ProductFiltersFormValues {
  // taking additional filters from product set in item
  const productTypeIds: string | undefined = !isUndefined(
    item?.pendingChangeAttributes["productTypeId"]
  )
    ? item?.pendingChangeAttributes["productTypeId"]
    : item?.product?.productTypeId;
  const productBrand: string | undefined = !isUndefined(
    item?.pendingChangeAttributes["productBrand"]
  )
    ? item?.pendingChangeAttributes["productBrand"]
    : item?.product?.brand;
  const productBrandLine: string | undefined = !isUndefined(
    item?.pendingChangeAttributes["productBrandLine"]
  )
    ? item?.pendingChangeAttributes["productBrandLine"]
    : item?.product?.brandLine;
  return {
    organisationFavourite: definitions.favouritesFilter?.valueBool ?? null,
    productTypeIds: definitions.productTypeIdFilter
      ? [definitions.productTypeIdFilter.valueId]
      : productTypeIds
        ? [productTypeIds]
        : null,
    supplierIds: definitions.supplierIdFilter
      ? definitions.supplierIdFilter.__typename === "EntitySearchIdFilter"
        ? [definitions.supplierIdFilter.valueId]
        : definitions.supplierIdFilter.valueIdIn
      : null,
    brands: definitions.brandFilter
      ? definitions.brandFilter.__typename === "EntitySearchTextFilter"
        ? [definitions.brandFilter.valueText]
        : definitions.brandFilter.valueTextIn
      : productBrand
        ? [productBrand]
        : null,
    brandLines: definitions.brandLineFilter
      ? definitions.brandLineFilter.__typename === "EntitySearchTextFilter"
        ? [definitions.brandLineFilter.valueText]
        : definitions.brandLineFilter.valueTextIn
      : productBrandLine
        ? [productBrandLine]
        : null,
    supplierProductStatuses: null,
    listPriceMax: definitions.listPriceFilter
      ? definitions.listPriceFilter.__typename === "EntitySearchNumberFilter" &&
        (definitions.listPriceFilter.operatorNumber === "lt" ||
          definitions.listPriceFilter.operatorNumber === "lte" ||
          definitions.listPriceFilter.operatorNumber === "eq")
        ? definitions.listPriceFilter.valueNumber
        : definitions.listPriceFilter.__typename ===
            "EntitySearchNumberBetweenFilter"
          ? definitions.listPriceFilter.valueNumberBetween.max
          : null
      : null,
    listPriceMin: definitions.listPriceFilter
      ? definitions.listPriceFilter.__typename === "EntitySearchNumberFilter" &&
        (definitions.listPriceFilter.operatorNumber === "gt" ||
          definitions.listPriceFilter.operatorNumber === "gte" ||
          definitions.listPriceFilter.operatorNumber === "eq")
        ? definitions.listPriceFilter.valueNumber
        : definitions.listPriceFilter.__typename ===
            "EntitySearchNumberBetweenFilter"
          ? definitions.listPriceFilter.valueNumberBetween.min
          : null
      : null,

    properties: definitions.propertyFilters.map(propertyFilter => {
      switch (propertyFilter.__typename) {
        case "EntitySearchPropertyFilterBoolFilter": {
          return {
            key: propertyFilter.key,
            boolFilter: {
              operatorBool: propertyFilter.operatorBool,
              valueBool: propertyFilter.valueBool,
            },
          };
        }
        case "EntitySearchPropertyFilterBoolInFilter": {
          return {
            key: propertyFilter.key,
            boolInFilter: {
              operatorBoolIn: propertyFilter.operatorBoolIn,
              valueBoolIn: propertyFilter.valueBoolIn,
            },
          };
        }
        case "EntitySearchPropertyFilterNumberBetweenFilter": {
          return {
            key: propertyFilter.key,
            numberBetweenFilter: {
              operatorNumberBetween: propertyFilter.operatorNumberBetween,
              valueNumberBetween: propertyFilter.valueNumberBetween,
            },
          };
        }
        case "EntitySearchPropertyFilterNumberFilter": {
          return {
            key: propertyFilter.key,
            numberFilter: {
              operatorNumber: propertyFilter.operatorNumber,
              valueNumber: propertyFilter.valueNumber,
            },
          };
        }
        case "EntitySearchPropertyFilterNumberInFilter": {
          return {
            key: propertyFilter.key,
            numberInFilter: {
              operatorNumberIn: propertyFilter.operatorNumberIn,
              valueNumberIn: propertyFilter.valueNumberIn,
            },
          };
        }
        case "EntitySearchPropertyFilterTextFilter": {
          return {
            key: propertyFilter.key,
            textFilter: {
              operatorText: propertyFilter.operatorText,
              valueText: propertyFilter.valueText,
            },
          };
        }
        case "EntitySearchPropertyFilterTextInFilter": {
          return {
            key: propertyFilter.key,
            textInFilter: {
              operatorTextIn: propertyFilter.operatorTextIn,
              valueTextIn: propertyFilter.valueTextIn,
            },
          };
        }
        default:
          assertNever(propertyFilter);
      }
    }),

    supplierProductCategoryKey: null,
    supplierCatalogues: null,
  };
}

export function useCanCreateSupplierCatalogueProduct() {
  const client = useApolloClient();
  const query = usePimSearchSupplierCatalogues__OrganisationWriteAccessQuery({
    client,
    fetchPolicy: "cache-and-network",
  });

  const supplierCatalogues = getDataOrNull(
    query.data?.pimSearchSupplierCatalogues
  )?.supplierCatalogues;

  return {
    canCreateSupplierCatalogueProduct: Boolean(
      supplierCatalogues && supplierCatalogues.length > 0
    ),
    loading: query.loading,
  };
}

export function useCanEditSupplierCatalogueProduct(
  supplierId: string,
  supplierCatalogueId: string
) {
  const client = useApolloClient();
  const query = usePimGetSupplierCatalogue__OrganisationWriteAccessQuery({
    client,
    variables: { supplierId, supplierCatalogueId },
    fetchPolicy: "cache-and-network",
  });

  const supplierCatalogue = getDataOrNull(
    query.data?.pimGetSupplierCatalogue
  )?.supplierCatalogue;

  return {
    canEditSupplierCatalogueProduct:
      supplierCatalogue &&
      supplierCatalogue.supplierCatalogueAccess.organisationCanWrite,
    loading: query.loading,
  };
}

export function useProductViewed({ client }: { client: ApolloClient<object> }) {
  const [pimAddSupplierProductViewedMutation] =
    usePimAddSupplierProductViewedMutation({ client });

  const addProductViewed = useCallback(
    async (product: { articleNumber: string; supplierId: string }) => {
      await pimAddSupplierProductViewedMutation({
        variables: {
          input: {
            articleNumber: product.articleNumber,
            supplierId: product.supplierId,
          },
        },
      });
    },
    [pimAddSupplierProductViewedMutation]
  );

  return {
    addProductViewed,
  };
}

export function useProductViewedEffect({
  client,
  product: { articleNumber, supplierId },
}: {
  client: ApolloClient<object>;
  product: { articleNumber: string; supplierId: string };
}) {
  const { addProductViewed } = useProductViewed({ client });

  useEffect(() => {
    addProductViewed({ articleNumber, supplierId });
  }, [articleNumber, supplierId, addProductViewed]);
}

export function useProductBreadcrumbs(
  supplierId: string | null,
  categoryKey: string | null,
  productTitle: string
) {
  const client = useApolloClient();

  const query = useProductBreadcrumbItems__ProductCategoryTreeQuery({
    client,
    variables: { supplierId: supplierId!, categoryKey: categoryKey! },
    skip: !supplierId || !categoryKey,
  });

  const breadcrumbItems = React.useMemo((): BreadcrumbItem[] => {
    const productParentCategories =
      getDataOrNull(
        (query.data ?? query.previousData)?.pimGetSupplierProductCategoryTree
      )?.tree?.parents ?? [];
    const productCategory = getDataOrNull(
      (query.data ?? query.previousData)?.pimGetSupplierProductCategoryTree
    )?.tree?.productCategory;

    const items: BreadcrumbItem[] = (
      productCategory
        ? [...reverse([...productParentCategories]), productCategory]
        : []
    ).map(category => ({
      title: category.title,
      to: {
        pathname: `/products/catalogues`,
        search: getUrlSearchParamsByParamsName(
          {
            ...productFiltersDefaultValue,
            supplierProductCategoryKey: category.key,
            supplierIds: [supplierId],
          },
          "filters"
        ).toString(),
      },
    }));
    if (items.length > 0) {
      items.push({ title: productTitle, to: "/" });
    }
    return items;
  }, [supplierId, productTitle, query.data, query.previousData]);

  return breadcrumbItems;
}
